<template>

    <Debugger/>

    <AOS type="fade-down" duration="1500" :persistent-attributes="false">

        <Menu ref="menu" class="app__menu"/>

    </AOS>

    <div class="font-body">

        <RouterView/>

    </div>

    <transition enter-from-class="--enter-from"
                enter-to-class="--enter-to"
                leave-active-class="--leave-active"
                @enter="duringCurtainEntering"
                @after-enter="afterCurtainEnter">

        <div v-show="isPageCurtainShowing" class="page-curtain" :style="{ background: curtainColor }"/>

    </transition>

    <Footer v-show="hasRouterViewMounted"/>

    <teleport to="head">

        <template v-if="!isBrowserMode">

            <template v-if="currentLocale === Locale.CHINESE">

                <link rel="preload"
                      :href="require('./scss/fonts/source-hans/SourceHanSansSC-Bold-subset.woff2')"
                      as="font"
                      type="font/woff2"
                      crossorigin>
                <link rel="preload"
                      :href="require('./scss/fonts/source-hans/SourceHanSansSC-Bold-subset.woff')"
                      as="font"
                      type="font/woff"
                      crossorigin>

            </template>

            <template v-else>

                <link rel="preload"
                      :href="require('./scss/fonts/mabry-pro/MabryPro-Bold.woff2')"
                      as="font"
                      type="font/woff2"
                      crossorigin>
                <link rel="preload"
                      :href="require('./scss/fonts/mabry-pro/MabryPro-Bold.woff')"
                      as="font"
                      type="font/woff"
                      crossorigin>

                <link rel="preload"
                      :href="require('./scss/fonts/gt-america-mono/GTAmericaMono-Regular.woff2')"
                      as="font"
                      type="font/woff2"
                      crossorigin>
                <link rel="preload"
                      :href="require('./scss/fonts/gt-america-mono/GTAmericaMono-Regular.woff')"
                      as="font"
                      type="font/woff"
                      crossorigin>

                <link rel="preload"
                      :href="require('./scss/fonts/gt-america-mono/GTAmericaMono-Medium.woff2')"
                      as="font"
                      type="font/woff2"
                      crossorigin>
                <link rel="preload"
                      :href="require('./scss/fonts/gt-america-mono/GTAmericaMono-Medium.woff')"
                      as="font"
                      type="font/woff"
                      crossorigin>

            </template>

        </template>

    </teleport>

</template>

<script lang="ts">

    import { defineComponent, onMounted, provide, readonly, ref } from 'vue'
    import { useRouter } from 'vue-router'
    import Debugger from './components/Debugger.vue'
    import Footer from './components/Footer.vue'
    import Menu from './components/Menu.vue'
    import { seamlessPageTransitionFlag } from './compositions/App'
    import { checkIsSeamlessPageTransition } from './compositions/checkIsSeamlessPageTransition'
    import { useProvideTeleportFunctionality } from './compositions/UseCaseHeader'
    import { Theme } from './enums/Theme'
    import { VueInstance } from './Interfaces/VueInstance'
    import { themeColor } from './themeColor'
    import { useTranslation } from './compositions/UseTranslation'
    import { Locale } from './enums/Locale'
    import { isBrowserMode } from './utils'

    export default defineComponent({
        name: 'App',
        components: {
            Footer,
            Menu,
            Debugger
        },
        setup() {

            const router = useRouter()
            const { currentLocale } = useTranslation()
            const { headers } = useProvideTeleportFunctionality()

            const isSeamlessPageTransition = ref<boolean>(false)

            provide(seamlessPageTransitionFlag, readonly(isSeamlessPageTransition))

            onMounted(() => {

                setTimeout(() => {

                    router.beforeEach((to, from) => {

                        isSeamlessPageTransition.value = checkIsSeamlessPageTransition(to, from, true)

                    })

                })

            })

            return {
                isBrowserMode,
                Locale,
                currentLocale,
                headers,
                isSeamlessPageTransition
            }

        },
        data() {

            return {
                isPageCurtainShowing: false,
                next: null as any,
                curtainColor: '',
                hasRouterViewMounted: false
            }

        },
        mounted() {

            setTimeout(() => {

                this.$router.beforeEach((to, from, next) => {

                    if (!this.isSeamlessPageTransition) {

                        this.curtainColor = themeColor[to.meta?.theme as Theme] || themeColor[Theme.Default]

                        if (Object.prototype.hasOwnProperty.call(to.params, 'gradient')) {

                            this.curtainColor = to.params.gradient

                        }

                        this.isPageCurtainShowing = true
                        this.next = next

                    } else {

                        next()

                    }

                })

            })

        },
        created() {

            this.$router.afterEach(to => {

                if (!this.hasRouterViewMounted) {

                    const initAOSEvent = new Event('init-aos')

                    this.hasRouterViewMounted = true

                    this.$nextTick(() => {

                        document.dispatchEvent(initAOSEvent)

                    })

                }

                if (!this.isSeamlessPageTransition) {

                    setTimeout(() => {

                        this.isPageCurtainShowing = false

                    })

                }

                setTimeout(() => {

                    (this as any).$aos.refreshHard()

                })

            })

        },
        methods: {
            duringCurtainEntering() {

                setTimeout(() => (this.$refs.menu as VueInstance)?.clearMenuBackground(), 400)

            },
            afterCurtainEnter() {

                this.next && this.next()
                this.next = null

            },
            hideMenu() {

                (this.$refs.menu as VueInstance)?.hideMenu()

            },
            showMenu() {

                (this.$refs.menu as VueInstance)?.showMenu()

            }
        }
    })

</script>

<style lang="scss">

    html:not(.no-js) [data-aos=fade-up] {

        transform: translate3d(0, 40px, 0);

    }

    @include component(app) {

        @include part(menu) {

            @apply fixed w-full mt-0 top-0;

        }

    }

    @include component(page-curtain) {

        position: fixed;
        top: 0;
        left: 0;
        z-index: 100;
        width: 100vw;
        height: 100vh;
        transition: transform 1s $ease-out-expo;
        will-change: transform;

        @screen md {

            z-index: 99;

        }

        @include option(enter-from) {

            transform: translateY(100%);

        }

        @include option(enter-to) {

            transform: translateY(0);

        }

        @include option(leave-active) {

            transition: none;

        }

    }

</style>
